import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';


import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { PresignedUrlResponse } from '../../../../../models/PresignedUrl';
import { usePutAttachCandidateHistoriesFile } from '../../../../../api-refactored/exam-configuration/usePutAttachCandidateHistoriesFile';
import { usePutStartCandidateHistoriesProcessing } from '../../../../../api-refactored/exam-configuration/usePutStartCandidateHistoriesProcessing';
import { usePutUploadToS3 } from '../../../../../api-refactored/s3-upload/usePutUploadFileToS3';
import { useGetDocumentUploadStatus } from '../../../../../api-refactored/document/useGetDocumentUploadStatus';
import { FileProcessingState, FileUploadState } from '../../../../../models/File';
import UploadingInProgress from './UploadingInProgress';
import DocumentUploader from '../../../../../modules/admin/components/CandidateBlacklist/DocumentUploader';
import { useGetFileProcessingStatus } from '../../../../../api-refactored/exam-configuration/useGetFileProcessingStatus';
import FileProcessingError from './FileProcessingError';
import DownloadTemplate from './DownloadTemplate';
import { uploadFile } from '../../../../../utils/FileUtils';
import {typeTheme} from "web-library/theme/helper/tssHelper"

export interface BulkUploadProps {
  modalState: boolean;
  resetDataTable: () => void;
  callSearchCandidateHistoriesMutate: () => void;
  selectedExamTypeUuid: string;
  onDialogClose: () => void;
  displayUploadSuccessSnackbar: (numberOfRecords: number) => void;
}

const useStyles = makeStyles()((theme) =>
({
  buttonContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  downloadButton: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  downloadTemplateButton: {
    padding: '12px 48px',
    lineHeight: theme.spacing(4),
  },
  uploadErrorSection: {
    '& img': {
      width: 45,
      height: 45,
    },
  },
  fileProcessingErrorSpacing: {
    paddingRight: theme.spacing(3),
  },
  dropZoneHelperTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(4),
  },
  dropZoneHelperText: {
    color: theme.color.text.textSecondary,
  },
  fileBadgeParentContainer: {
    height: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  fileBadgeContainer: {
    height: theme.spacing(4),
    flexWrap: 'nowrap'
  },
  description: {
    marginBottom: theme.spacing(4),
  },
  uploadErrorDescription: {
     ...typeTheme(theme, 'body1bold'),
  },
  dropZone: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
    width: '100%',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    justifySelf: 'start',
    marginRight: theme.spacing(2),
  },
  documentIcon: {
    color: theme.color.text.textTertiary,
  },
  filename: {
    width: theme.spacing(3),
    textOverflow: 'ellipsis',
    flexGrow: 2,
    fontWeight: 'bold',
    color: theme.color.link.linkDefault01,
  },
  fileSize: {
    width: theme.spacing(15),
    justifySelf: 'start',
    color: theme.color.text.textTertiary,
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(2),
    },
  },
  removeCell: {
    width: theme.spacing(12),
    marginRight: theme.spacing(32),
  },
  removeButton: {
    padding: 0,
    height: theme.spacing(4),
    color: theme.color.icon.iconTrash,
  },
  uploadingDescription: {
    paddingBottom: theme.spacing(7),
  },
  fileProcessingErrorSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing(4),
  },
}));

const INITIAL_UPLOAD_PRESIGNED_DATA = {
  key: '',
  presignedUrl: '',
};

const UploadCandidateHistoriesModal = ({
  selectedExamTypeUuid,
  modalState,
  onDialogClose,
  resetDataTable,
  callSearchCandidateHistoriesMutate,
  displayUploadSuccessSnackbar,
}: BulkUploadProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const [uploadPresigned, setUploadPresigned] = useState<PresignedUrlResponse>(
    INITIAL_UPLOAD_PRESIGNED_DATA
  );
  const [pollGetFileStatus, setPollGetFileStatus] = useState<boolean>(false);
  const [showUploadingHolder, setShowUploadingHolder] = useState<boolean>(false);

  const uploadMutation = usePutUploadToS3();

  const fileUuid = uploadPresigned?.key;

  const putAttachedFileMutation = usePutAttachCandidateHistoriesFile({
    onSuccess: (res) => {
      if (res.key && res.presignedUrl) {
        uploadFile(
          res,
          uploadedFiles.map((uploadedFile) => uploadedFile.file),
          uploadMutation.mutate
        );
        setUploadPresigned(res);
      }
    },
  });

  const startFileProcessingMutation = usePutStartCandidateHistoriesProcessing({
    onSuccess: () => {
      setPollGetFileStatus(true);
    },
  });

  const getDocumentUploadStatus = useGetDocumentUploadStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid && startFileProcessingMutation.isIdle,
    refetchInterval: 5000,
  });

  const getFileProcessingStatus = useGetFileProcessingStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid && getDocumentUploadStatus.isSuccess && pollGetFileStatus,
    refetchInterval: 5000,
  });

  const fileProcessingStatus = getFileProcessingStatus.data?.status;
  const isFileProcessingError = fileProcessingStatus === FileProcessingState.ERROR;

  const uploadFileClickHandler = () => {
    setShowUploadingHolder(true);
    startFileProcessingMutation.reset();
    putAttachedFileMutation.mutate({
      fileName: uploadedFiles[0].file.name,
    });
  };

  const fileStatus = getDocumentUploadStatus.data?.fileStatus;
  const isUploadFailed = getDocumentUploadStatus.data?.fileStatus === FileUploadState.FAILED;

  useEffect(() => {
    if (fileStatus === FileUploadState.SUCCESS && fileUuid) {
      startFileProcessingMutation.mutate({ uuid: fileUuid, examTypeUuid: selectedExamTypeUuid });
    } else if (fileStatus === FileUploadState.FAILED && fileUuid) {
      setShowUploadingHolder(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fileStatus,
    fileUuid,
    selectedExamTypeUuid,
    startFileProcessingMutation.mutate,
    setShowUploadingHolder,
  ]);

  const resetUploadAndProcessingApi = () => {
    setUploadPresigned(INITIAL_UPLOAD_PRESIGNED_DATA);
    startFileProcessingMutation.reset();
    putAttachedFileMutation.reset();
    uploadMutation.reset();
    getFileProcessingStatus.remove();
    getDocumentUploadStatus.remove();
    setPollGetFileStatus(false);
    setShowUploadingHolder(false);
  };

  useEffect(() => {
    if (fileProcessingStatus === FileProcessingState.SUCCESS && fileUuid) {
      resetUploadAndProcessingApi();
      onDialogClose();
      displayUploadSuccessSnackbar(getFileProcessingStatus.data?.numberOfRecords || 0);
      resetDataTable();
      callSearchCandidateHistoriesMutate();
    } else if (fileProcessingStatus === FileProcessingState.ERROR && fileUuid) {
      setPollGetFileStatus(false);
      setShowUploadingHolder(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileProcessingStatus, setPollGetFileStatus, onDialogClose, fileUuid, resetDataTable]);

  const conditionToShowDocumentUploader =
    uploadMutation.isIdle || isFileProcessingError || isUploadFailed;

  const disabledUploadButton = showUploadingHolder || !uploadedFiles.length;
  const disabledBackButton = showUploadingHolder;

  const onCancel = () => {
    onDialogClose();
    resetUploadAndProcessingApi();
  };

  return (
    <Dialog open={modalState} maxWidth="md">
      <DialogTitle>
        <Typography variant="h4">
          {t('exam->>ExamConfiguration.UploadCandidateListTitle')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {conditionToShowDocumentUploader && (
          <>
            {isFileProcessingError ? (
              <FileProcessingError
                url={getFileProcessingStatus.data?.presignUrl || ''}
                classes={classes}
              />
            ) : (
              <Box>
                <DialogContentText>
                  {t('exam->>ExamConfiguration.UploadCandidateListDescription')}
                </DialogContentText>
                <DownloadTemplate classes={classes} />
              </Box>
            )}

            <DocumentUploader
              key={getDocumentUploadStatus.data?.fileStatus}
              fileStatus={getDocumentUploadStatus.data?.fileStatus}
              classes={classes}
              translation={t}
              stateHandler={{
                setUploadedFileHandler: { uploadedFiles, setUploadedFiles },
              }}
              apiHooks={{
                uploadMutation,
              }}
              showUploadInstruction={false}
            />
          </>
        )}
        {showUploadingHolder && (
          <UploadingInProgress
            classes={classes}
            uploadedFiles={uploadedFiles.map((uploadedFile) => uploadedFile.file)}
          />
        )}
      </DialogContent>
      <DialogActions id="oppadmin-dialog-action">
        <Box className={classes.buttonContainer}>
          <Button onClick={onCancel} variant="text" disabled={disabledBackButton}>
            {t('exam->>ExamConfiguration.Cancel')}
          </Button>
          <Button
            startIcon={<Icon name={IconName.export} size="S" color="primary" />}
            type="button"
            onClick={uploadFileClickHandler}
            disabled={disabledUploadButton}
            id="button-upload"
          >
            {t('exam->>ExamConfiguration.UploadFile')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UploadCandidateHistoriesModal;
